import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check, Share2, Mail, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useOverlayState } from '../context/OverlayContext';
import toast from 'react-hot-toast';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  useOverlayState(isOpen, 'invite-modal');
  const [copied, setCopied] = useState(false);

  const baseUrl = import.meta.env.VITE_APP_URL || 'https://vybetribe.app';
  const referralCode = currentUser?.username || currentUser?.id || 'invite';
  const inviteLink = `${baseUrl}/?ref=${referralCode}`;

  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Join VybeTribe',
      text: 'Join me on VybeTribe - Connect with people and discover local experiences!',
      url: inviteLink,
    };

    if (navigator.share && navigator.canShare?.(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          handleCopy();
        }
      }
    } else {
      handleCopy();
    }
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent('Join me on VybeTribe!');
    const body = encodeURIComponent(
      `Hi!\n\nI've been using VybeTribe to connect with people and discover local experiences. I think you'd love it too!\n\nJoin me here: ${inviteLink}\n\nLooking forward to connecting!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(
      `Join me on VybeTribe! Connect with people and discover local experiences: ${inviteLink}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const portalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          <div
            className="nav-popover-backdrop"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="nav-popover-panel rounded-2xl border border-theme-cards bg-theme-cards shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <Share2 size={20} className="mr-2 text-theme-primary" />
                  <h2 className="text-lg font-semibold text-white">Invite Friends</h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-theme-text-secondary hover:text-white p-1"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              <p className="text-theme-text-secondary text-sm mb-4">
                Share VybeTribe with your friends! They'll be able to join using your personal invite link.
              </p>

              {/* Invite Link */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-theme-text-secondary mb-2">
                  Your Invite Link
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={inviteLink}
                    readOnly
                    className="flex-1 p-2.5 bg-theme-bg border border-theme-cards rounded-lg text-white text-sm"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <button
                    onClick={handleCopy}
                    className={`p-2.5 rounded-lg transition-all flex items-center justify-center min-w-[44px] ${
                      copied
                        ? 'bg-green-500 text-white'
                        : 'bg-theme-primary text-white hover:bg-theme-primary/90'
                    }`}
                    aria-label="Copy link"
                  >
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                  </button>
                </div>
              </div>

              {/* Share Options */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-theme-text-secondary mb-2">Share via:</p>

                <button
                  onClick={handleShare}
                  className="w-full flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-all font-medium text-sm"
                >
                  <Share2 size={18} />
                  Share Link
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleEmailShare}
                    className="flex items-center justify-center gap-2 p-2.5 bg-theme-bg hover:bg-theme-bg/80 text-white rounded-lg transition-all"
                  >
                    <Mail size={18} />
                    <span className="text-sm font-medium">Email</span>
                  </button>

                  <button
                    onClick={handleWhatsAppShare}
                    className="flex items-center justify-center gap-2 p-2.5 bg-green-500/10 hover:bg-green-500/20 text-green-500 rounded-lg transition-all"
                  >
                    <MessageCircle size={18} />
                    <span className="text-sm font-medium">WhatsApp</span>
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <p className="text-xs text-blue-400">
                  <strong>Pro Tip:</strong> Friends who sign up with your link will be added to your network automatically!
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(portalContent, document.body);
};

export default InviteModal;
